/* pp-layoutpicker.js — choose the starting layout on the PRODUCT page and go
   straight into the editor, skipping Mediaclip's theme chooser.

   Jeff: "with a little fleshing out, this should be the product page, really.
   you select your variation right there and are taken to the editor."

   HOW IT SKIPS THE CHOOSER. The designer is launched by a form POST that our
   own theme builds (see pp-personalize.js). Adding one hidden field to that
   POST preselects the theme and Mediaclip drops the chooser step:

       mediaclipThemeUrlOverride = $(package:mediaclip/photobooks)/themes/...

   ⚠ PROVEN, NOT ASSUMED. Measured on prod: with the field set, the chooser
   markup (.themes-container) is absent and the editor opens; quickbook-white
   yields a page with 1 photo frame and blank-js yields 0, so the requested
   theme is genuinely applied rather than a default.

   ⚠ SCOPE IS THE LAYFLAT FAMILY ONLY. Theme sets are PER PRODUCT, not per
   package: the 8x8 Hard Cover offers a completely different set of 21+. All
   six Layflat products were checked individually and carry the identical 18,
   which is why one list is safe here and nowhere else yet. A product not in
   SETS keeps today's behavior untouched.

   ⛔ ONE-WAY DOOR. The editor's rail has no THEME entry (allowChangeTheme is
   off for this store), so a preselected layout cannot be changed from inside.
   That is why "Browse all 18 designs" stays on the page: it launches WITHOUT
   the override, which is exactly today's flow. */
(function () {
  'use strict';

  var VERSION = '2026.09.15.24-sticky';
  var CARDS = [];
  var CAPTION = false;
  var ENDPOINT = '/tools/customize/create-project';

  /* ── WHICH PRODUCTS GET A PICKER, AND WITH WHAT ──────────────────────────
     Jeff: "give every product this treatment."

     MEASURED across the catalog. A product's theme set is decided by its
     Mediaclip PACKAGE, and its package is the slugified Shopify product_type
     — checked for all 25 published types, all of them under module=Print
     except the photobooks. A Mediaclip productId is
     `$(package:myseum/<package>)/products/<Fuji SKU>`.

     ⛔ THE COUNTS ARE WHY THIS IS NOT ONE UNIFORM TREATMENT:

         cards                                   156 themes
         mugs                                     97
         prints, bags, blankets, magnets, pillows 53
         calendars-wall-mounted                   32
         office                                   22
         photobooks / photobooks-layflat          21 / 18
         everything else (15 packages)             2

     The 2-theme packages ALL share one pair — `core/blank` and
     `core/full-size-photo` — so a two-tile picker covers most of the catalog
     honestly, with drawings that cannot be wrong.

     The big sets are real designs ("Monogram (Blue)", "A Sweet Friendship
     Refreshes the Soul"), not layouts. They are NOT listed here on purpose:
     a hundred hand-drawn tiles would be a hundred guesses, and those products
     keep today's flow until their real previews are harvested and hosted.
     See the memory note for the harvesting route. */

  var PB = '$(package:mediaclip/photobooks)/themes/';
  var LF = '$(package:mediaclip/photobooks-layflat)/themes/';
  var CORE = '$(package:mediaclip/core)/themes/';

  /* The pair every flat product shares. A print has ONE surface, so its
     swatches show one sheet, not a book spread. */
  var FLAT = [
    { name: 'Full photo',  art: 'flatfull',  theme: CORE + 'full-size-photo',
      note: 'Your photo, edge to edge' },
    { name: 'Design your own', art: 'flatblank', theme: CORE + 'blank',
      note: 'Start from a blank page' }
  ];

  var BOOK = [
    { name: 'Full page photos',    art: 'full',   theme: PB + 'fullpagephoto-js',
      note: 'One photo fills each page' },
    { name: 'Quickbook',           art: 'one',    theme: PB + 'quickbook-white',
      note: '1 photo per page' },
    { name: 'Quickbook - Black',   art: 'one',    theme: PB + 'quickbook-black',
      note: '1 photo per page, dark', dark: true },
    { name: 'Positive Space',      art: 'onetwo', theme: PB + 'positive-space',
      note: '1 or 2 photos per page' },
    { name: 'Panoramic',           art: 'pano',   theme: PB + 'panoramic',
      note: 'Horizontal photos only' },
    { name: 'Wedding Guest Book',  art: 'guest',  theme: LF + 'guestbook-modern',
      note: 'With photos inside texts' },
    { name: 'Focus - White',       art: 'focus',  theme: PB + 'focus-white' },
    { name: 'Focus - Black',       art: 'focus',  theme: PB + 'focus-black',
      note: 'Dark pages', dark: true },
    { name: 'Collection - White',  art: 'collection', theme: PB + 'collection-white' },
    { name: 'Collection - Black',  art: 'collection', theme: PB + 'collection-black',
      note: 'Dark pages', dark: true },
    { name: 'Artistic White',      art: 'artistic',   theme: LF + 'artistic-white' },
    { name: 'Artistic Black',      art: 'artistic',   theme: LF + 'artistic-black',
      note: 'Dark pages', dark: true },
    { name: 'Sophisticated White', art: 'sophisticated', theme: LF + 'sophisticated-white' },
    { name: 'Sophisticated Black', art: 'sophisticated', theme: LF + 'sophisticated-black',
      note: 'Dark pages', dark: true },
    { name: 'Stylish White',       art: 'stylish', theme: LF + 'stylish-white' },
    { name: 'Stylish Black',       art: 'stylish', theme: LF + 'stylish-black',
      note: 'Dark pages', dark: true },
    { name: 'Quad Color',          art: 'quad',   theme: LF + 'quadcolor' },
    { name: 'Blank',               art: 'blank',  theme: PB + 'blank-js',
      note: 'Design your own' }
  ];

  /* package -> the cards to show. A package that is absent gets no picker and
     keeps the store's existing Personalize flow, untouched. */
  /* ⚠ ONLY PACKAGES WHOSE THEME IDS I HAVE ACTUALLY SEEN COME BACK 200.
     ★ plaques, wall-decor and wallet-prints are now confirmed as the core
     pair and are in. ⛔ PUZZLES IS NOT: it turned out to have 53 themes, not
     2 — which is exactly why unverified packages were held back rather than
     assumed from the pattern. Packages with big design sets are absent until
     their real previews are hosted. */
  var SETS = {
    'photobooks-layflat': BOOK,
    'acrylic-prints': FLAT,
    'colored-edge-mounted-prints': FLAT,
    'desktop-canvas': FLAT,
    'framed-prints': FLAT,
    'gallery-wrapped-canvas-3pg': FLAT,
    'gifts': FLAT,
    'large-prints': FLAT,
    'metal-wall-decor': FLAT,
    'ornaments': FLAT,
    'plaques': FLAT,
    'premium-mounted-prints': FLAT,
    'standard-prints': FLAT,
    'wall-decor': FLAT,
    'wallet-prints': FLAT,
  };

  /* Shopify product_type -> Mediaclip package. The slug IS the package name;
     this map exists only so the type can be read off the page. */
  function slug(t) {
    return String(t || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  /* ★ THE PRODUCT TYPE COMES FROM ShopifyAnalytics.
     MEASURED, after two wrong guesses: there is no meta tag, no data
     attribute, and the page's JSON-LD Product node carries NO `category` —
     the "category" visible in the raw HTML belongs to Shopify's analytics
     payload. `window.ShopifyAnalytics.meta.product.type` is exactly the
     Shopify product_type ("Photobooks Layflat"), and its slug is the
     Mediaclip package name. A regex over the document is the fallback for the
     case where the analytics object has not initialised yet; the observer
     re-runs mount() either way, so a late arrival is not a miss. */
  function productPackage() {
    var t = '';
    try {
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta &&
          window.ShopifyAnalytics.meta.product) {
        t = window.ShopifyAnalytics.meta.product.type || '';
      }
    } catch (e) {}
    if (!t) {
      try {
        var m = /"category"\s*:\s*"([^"]{1,60})"/.exec(document.documentElement.innerHTML);
        if (m) t = m[1];
      } catch (e) {}
    }
    return slug(t);
  }

  /* -- THE PER-PRODUCT SET FILE --------------------------------------------
     ⚠ MEASURED, AND IT CONTRADICTS THE MAP ABOVE: a theme list belongs to a
     PRODUCT, not to a package. getthemes was replayed for all 363 published
     variants; within `photobooks` alone the 8x8 Hard Cover answers 48 themes,
     the 7x7 Softcover 55 (ten of them under `fujifilm/photobooks`, not
     `mediaclip/`), and every Layflat 18. Offering a product a layout it does
     not carry would post a theme id its package cannot resolve, so the list
     is looked up by variant id first and handle second.

     The file is a theme asset so it is same-origin and Shopify versions the
     URL; the shape is
         { img: "<cdn prefix>", lists: { "<id>": [entry, ...] },
           variant: { "<variant id>": "<list id>" },
           handle:  { "<handle>": "<list id>" } }
     and an entry is [leaf, name, package, note, artKind]. `artKind` present
     means draw it (the two-theme flat products keep their drawings); absent
     means use <cdn prefix><leaf>.webp, which is Mediaclip's own render.

     SETS stays as the fallback: if this fetch fails the picker is exactly
     what shipped yesterday rather than absent. */
  var DATA = null;
  var DATA_STATE = 'idle';        /* idle | pending | ready | failed */

  function setsUrl() {
    if (window.PP_LP_SETS_URL) return window.PP_LP_SETS_URL;
    /* Same folder as this script, which Liquid stamped with a version. */
    try {
      var me = document.querySelector('script[src*="pp-layoutpicker.js"]');
      if (me) return me.src.replace(/pp-layoutpicker\.js.*$/, 'pp-sets.json');
    } catch (e) {}
    return null;
  }

  function loadSets(then) {
    if (DATA_STATE !== 'idle') return;
    var u = setsUrl();
    if (!u || !window.fetch) { DATA_STATE = 'failed'; return then(); }
    DATA_STATE = 'pending';
    var settled = false;
    function done(d) {
      if (settled) return;
      settled = true;
      DATA = d;
      DATA_STATE = d ? 'ready' : 'failed';
      then();
    }
    /* ⚠ A hung CDN must not cost the customer the picker. */
    setTimeout(function () { done(null); }, 4000);
    fetch(u, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(done, function () { done(null); });
  }

  function variantId() {
    try {
      var i = document.querySelector('form[action*="/cart/add"] [name="id"]');
      if (i && i.value) return String(i.value);
    } catch (e) {}
    return null;
  }

  /* [leaf, name, package, note, artKind] -> the card build() wants. */
  function entryCard(e, img) {
    return {
      name: e[1],
      note: e[3] || '',
      theme: '$(package:' + e[2] + ')/themes/' + e[0],
      art: e[4] || null,
      /* ⚠ e[5], NOT the leaf. One theme has one render PER PRODUCT PACKAGE --
         `mediaclip/core/01` is a tote bag on the bag and a framed photo on
         the frame -- so the file is named for the set, not the theme. */
      img: e[4] ? null : (img + e[5] + '.webp'),
      /* The dark layouts are named for it, and the shade only tints the
         drawings -- a real render carries its own color. */
      dark: !e[4] ? false : /black|charcoal|dark/i.test(e[0])
    };
  }

  var SET_ID = null;

  function listFromData() {
    if (!DATA || !DATA.lists) return null;
    var vid = variantId(), h = handle(), id = null;
    if (vid && DATA.variant) id = DATA.variant[vid] || null;
    if (!id && h && DATA.handle) id = DATA.handle[h] || null;
    var l = id && DATA.lists[id];
    if (!l || !l.length) return null;
    SET_ID = id;
    return l.map(function (e) { return entryCard(e, DATA.img || ''); });
  }

  /* ⛔ THE PRINT MODULE GETS NO PICKER, BECAUSE PRESELECTING A THEME BREAKS IT.
     MEASURED 2026-09-18, twice each way on mc-prgift-5475-matte:

       launched WITH mediaclipThemeUrlOverride  -> add a photo -> POST
         ecb.mediacliphub.com/api/autofill answers 500
         {"code":"ServiceError","message":"An unexpected error occurred and
          has been logged for the maintenance team."}
       launched WITHOUT it -> add a photo -> works.

     Same product, same photo, the override the only difference. It is not the
     photo source (a device upload failed identically), not the quantity, and
     not the product id. Gifting products are unaffected because they drop one
     photo into a fixed frame; the Print module has to autofill N prints
     against the preselected theme, and that is the path that throws.

     THE PICKER IS HIDDEN, NOT JUST THE OVERRIDE. Showing twelve designs and
     then silently ignoring the one you pick is worse than not offering them:
     prints fall back to Mediaclip's own theme chooser, which is what they had
     before this file existed.

     ⚠ EVERY module-print product is productType "Prints" -- all 39 of them,
     checked against the catalog -- and nothing else uses that type, so the
     slug is a safe test. If a print product ever ships under another type
     this stops protecting it.
     ⭐ WORTH RETESTING once Mediaclip fixes autofill: all 53 themes in the
     l2-prints set are `mediaclip/core` ones, so it may be core themes
     specifically that the print module cannot take. If a prints-package theme
     works, the picker can come back for prints. */
  var NO_PICKER_PACKAGES = { prints: 1 };

  /* ⛔ PRINTS GET NO PICKER, AND THE 500 IS CONFIRMED STILL LIVE 2026-09-22.
     Re-measured today by Jeff via a temporary ?pplayouts=1 flag (now removed):
     a 4x6 Matte launched WITH mediaclipThemeUrlOverride, then one photo added,
     answers "Error 500", runId 7435f7215b0e257aae81e04a652d4a6c. That is four
     days after the first measurement on 2026-09-18, so it is not transient and
     it survived Mediaclip's 2026.9.17.01 build.
     ⚠ THE EXCLUSION STAYS UNTIL MEDIACLIP FIXES /api/autofill. It costs 53
     print layouts, which is the right trade against an error on add-photo.
     ★ The test is cheap to repeat: add the flag back, tap a layout, add a
     photo. ⛔ And it can ONLY be run in the TESTER'S OWN BROWSER -- Mediaclip
     binds a project to the Hub user, which is per-browser, so a projectId from
     someone else's session answers 403 "you are not the owner". */
  function cardsForThisProduct() {
    var pkg = productPackage();
    if (NO_PICKER_PACKAGES[pkg]) return { pkg: pkg, cards: null };
    return { pkg: pkg, cards: listFromData() || SETS[pkg] || null };
  }

  /* ── The swatch: A COVER AND AN INSIDE SPREAD, ONE PER THEME ──────────
     Jeff: "too many of these layout thumbnails look identical. please make
     them match the real world counterpart."

     He was right: eleven of the eighteen shared one generic collage, because
     I had never actually seen those layouts. I have now — I collapsed the
     intro in designer.css so all three rows of Mediaclip's previews fit one
     viewport and zoomed each row. Every shape below is read off the real
     preview, not inferred from the name.

     THE FORMAT (measured): a square COVER on the left, full bleed to its
     edge, usually with a "Your Text Here" caption bar sitting on it, plus a
     landscape INSIDE SPREAD on the right with a center fold and wide page
     margins, on a white or black ground.

     WHAT DISTINGUISHES EACH ONE (measured):
       Focus         a HEART-shaped photo collage on the cover
       Collection    cover photo with a caption BAND beneath it, grid spread
       Artistic      every photo sits inside a wide mat
       Sophisticated full-bleed cover, few small photos, lots of empty page
       Stylish       a mixed-size collage, three across the top
       Quad Color    colored blocks interleaved with the photos
       Guest book    script text beside the photos
     Blank, Full page photos, Quickbook, Positive Space and Panoramic were
     read the same way in the first pass. */
  function art(kind) {
    var CX = 7, CY = 10, CW = 52, CH = 52;      /* cover */
    var SX = 67, SY = 15, SW = 54, SH = 42;     /* inside spread */

    function rect(cls, x, y, w, h, rad) {
      return '<rect class="' + cls + '" x="' + x + '" y="' + y + '" width="' + w +
             '" height="' + h + '" rx="' + (rad === undefined ? 1 : rad) + '"/>';
    }
    /* fractions of the spread box, so an arrangement stays readable at 130px */
    function f(fx, fy, fw, fh, cls) {
      return rect(cls || 'pp-lp__ph', SX + SW * fx, SY + SH * fy, SW * fw, SH * fh, 1);
    }
    function ln(fx, fy, fw) {
      return '<line class="pp-lp__ln" x1="' + (SX + SW * fx) + '" y1="' + (SY + SH * fy) +
             '" x2="' + (SX + SW * (fx + fw)) + '" y2="' + (SY + SH * fy) + '"/>';
    }

    var page = rect('pp-lp__pg', CX, CY, CW, CH, 2.5);
    var cap  = rect('pp-lp__cap', CX + 11, CY + CH - 13, CW - 22, 6, 1);

    /* covers */
    function coverBleed(text) {
      return page + rect('pp-lp__ph', CX, CY, CW, CH, 2.5) + (text ? cap : '');
    }
    function coverEmpty() { return page; }
    /* Collection: the photo stops short and a caption band sits under it */
    function coverBand() {
      return page + rect('pp-lp__ph', CX, CY, CW, CH * 0.76, 2.5) +
             rect('pp-lp__cap', CX + 9, CY + CH * 0.84, CW - 18, 5, 1);
    }
    /* Artistic: the photo sits inside a wide mat */
    function coverMat() {
      return page + rect('pp-lp__ph', CX + 7, CY + 6, CW - 14, CH - 20, 1) +
             rect('pp-lp__cap', CX + 13, CY + CH - 11, CW - 26, 5, 1);
    }
    /* Quad Color: the caption bar is a color block, not paper */
    function coverColor() {
      return page + rect('pp-lp__ph', CX, CY, CW, CH, 2.5) +
             rect('pp-lp__accent', CX + 9, CY + CH - 14, CW - 18, 7, 1);
    }
    /* Focus: a heart-shaped collage fills the cover */
    function coverHeart() {
      var m = 7, w = CW - m * 2, h = CH - m * 2 - 4, x = CX + m, y = CY + m;
      function P(ux, uy) { return (x + ux * w).toFixed(1) + ',' + (y + uy * h).toFixed(1); }
      var d = 'M' + P(.5, .95) +
              'C' + P(.2, .75) + ' ' + P(.02, .55) + ' ' + P(.02, .36) +
              'C' + P(.02, .15) + ' ' + P(.28, .07) + ' ' + P(.5, .27) +
              'C' + P(.72, .07) + ' ' + P(.98, .15) + ' ' + P(.98, .36) +
              'C' + P(.98, .55) + ' ' + P(.8, .75) + ' ' + P(.5, .95) + 'Z';
      return page + '<path class="pp-lp__ph" d="' + d + '"/>' +
             rect('pp-lp__cap', CX + 13, CY + CH - 10, CW - 26, 4, 1);
    }

    function spread(frames) {
      return rect('pp-lp__pg', SX, SY, SW, SH, 2) +
             '<line class="pp-lp__spine" x1="' + (SX + SW / 2) + '" y1="' + SY +
             '" x2="' + (SX + SW / 2) + '" y2="' + (SY + SH) + '"/>' + frames;
    }

    /* A flat product is ONE surface. Drawing a book spread for a framed print
       would be a lie about the object. */
    function sheet(inner) {
      var SW2 = 60, SH2 = 46, SX2 = 34, SY2 = 13;
      return rect('pp-lp__pg', SX2, SY2, SW2, SH2, 2.5) +
             (inner ? inner(SX2, SY2, SW2, SH2) : '');
    }

    var body;
    switch (kind) {
      case 'flatfull':
        body = sheet(function (x, y, w, h) {
          return rect('pp-lp__ph', x, y, w, h, 2.5);
        });
        break;
      case 'flatblank':
        body = sheet(null);
        break;
      case 'blank':
        body = coverEmpty() + spread('');
        break;
      case 'full':          /* one band across the top, a pair beneath */
        body = coverBleed(false) + spread(
          f(.06, .10, .88, .38) + f(.06, .56, .42, .34) + f(.52, .56, .42, .34));
        break;
      case 'one':           /* four matted images, one per page */
        body = coverBleed(true) + spread(
          f(.10, .14, .32, .32) + f(.58, .14, .32, .32) +
          f(.10, .54, .32, .32) + f(.58, .54, .32, .32));
        break;
      case 'onetwo':        /* a single image left, a stacked pair right */
        body = coverBleed(true) + spread(
          f(.08, .18, .36, .64) + f(.56, .14, .36, .30) + f(.56, .52, .36, .30));
        break;
      case 'pano':          /* horizontal bands */
        body = coverBleed(false) + spread(
          f(.06, .14, .88, .30) + f(.06, .56, .88, .30));
        break;
      case 'guest':         /* script text beside the photos */
        body = coverBleed(false) + spread(
          f(.08, .12, .36, .40) + ln(.08, .66, .30) + ln(.08, .78, .34) + ln(.08, .90, .22) +
          f(.56, .12, .36, .40) + ln(.56, .66, .34) + ln(.56, .78, .28) + ln(.56, .90, .32));
        break;
      case 'focus':         /* heart cover; paired photos inside */
        body = coverHeart() + spread(
          f(.08, .12, .36, .36) + f(.56, .12, .36, .36) +
          f(.08, .56, .36, .32) + f(.56, .56, .36, .32));
        break;
      case 'collection':    /* caption band cover; a grid of small photos */
        body = coverBand() + spread(
          f(.07, .13, .25, .32) + f(.36, .13, .25, .32) + f(.65, .13, .28, .32) +
          f(.07, .55, .25, .32) + f(.36, .55, .25, .32) + f(.65, .55, .28, .32));
        break;
      case 'artistic':      /* everything inside a wide mat */
        body = coverMat() + spread(
          f(.12, .18, .28, .30) + f(.60, .18, .28, .30) +
          f(.12, .58, .28, .26) + f(.60, .58, .28, .26));
        break;
      case 'sophisticated': /* few, small, lots of empty page */
        body = coverBleed(true) + spread(
          f(.14, .16, .22, .26) + f(.62, .16, .22, .26) +
          f(.14, .58, .30, .24));
        break;
      case 'stylish':       /* mixed sizes, three across the top */
        body = coverBleed(true) + spread(
          f(.06, .12, .20, .30) + f(.28, .12, .16, .30) +
          f(.56, .12, .36, .30) +
          f(.06, .50, .38, .36) + f(.56, .50, .16, .36) + f(.74, .50, .18, .36));
        break;
      case 'quad':          /* color blocks interleaved with photos */
        body = coverColor() + spread(
          f(.07, .12, .34, .34) + f(.07, .54, .16, .32, 'pp-lp__accent') +
          f(.25, .54, .16, .32) +
          f(.56, .12, .16, .34, 'pp-lp__accent') + f(.74, .12, .18, .34) +
          f(.56, .54, .36, .32));
        break;
      default:
        body = coverBleed(true) + spread(
          f(.07, .12, .38, .34) + f(.55, .12, .38, .34) +
          f(.07, .54, .38, .34) + f(.55, .54, .38, .34));
    }
    return '<svg class="pp-lp__art" viewBox="0 0 128 72" aria-hidden="true">' + body + '</svg>';
  }

  function handle() {
    var m = /\/products\/([^/?#]+)/.exec(location.pathname);
    return m ? m[1] : null;
  }

  /* Reuse the real button's own form so the product and variant ids are
     whatever Shopify already put there, rather than anything we guess. */
  function launch(themeUrl) {
    var real = document.querySelector('button[data-is-customize-button="true"]');
    var src = real && real.closest('form');
    if (!src) return false;
    var f = document.createElement('form');
    f.method = 'post';
    f.action = ENDPOINT;
    f.style.display = 'none';
    var wanted = { form_type: 1, utf8: 1, id: 1, 'product-id': 1, quantity: 1 };
    var seen = {};
    [].slice.call(src.querySelectorAll('input,select')).forEach(function (i) {
      var n = i.getAttribute('name');
      if (!n || !wanted[n] || seen[n]) return;
      seen[n] = 1;
      var h = document.createElement('input');
      h.type = 'hidden'; h.name = n; h.value = i.value;
      f.appendChild(h);
    });
    /* ⛔ DO NOT REINTRODUCE A DEFAULT QUANTITY. This used to append
       quantity=1 whenever the real form had none, which put
       data-cartQuantity=1 into the designer URL and on into Mediaclip's
       autofill call. Mediaclip's OWN customize button posts no quantity at
       all -- its form is form_type, utf8, id, product-id and nothing else --
       so we were asserting a number they never asked for. It is wrong twice
       over on prints: the Print module derives quantity from how many photos
       you add ("Each photo you add becomes one print"), and these products
       carry mediaclip/quantity_rules with a MINIMUM OF 10, which nothing in
       this theme reads. Posting without it yields a designer URL with no
       cartQuantity, which is what their button produces. */
    /* themeUrl is always set now that the fallback link is gone; the guard
       stays so launch() cannot silently post a bare project if that changes. */
    if (themeUrl) {
      var t = document.createElement('input');
      t.type = 'hidden'; t.name = 'mediaclipThemeUrlOverride'; t.value = themeUrl;
      f.appendChild(t);
    }
    document.body.appendChild(f);
    try { window.dispatchEvent(new CustomEvent('pp:navigating')); } catch (e) {}
    f.submit();
    return true;
  }

  /* One hidden SVG holding the gradients every swatch fills with. Fragment
     ids resolve document-wide, so the twelve inline SVGs can all point at
     these two. A flat block reads as a placeholder; a soft wash reads as a
     photograph, which is the difference between a wireframe and something
     that resembles the real preview. */
  function defs() {
    if (document.getElementById('pp-lp-defs')) return;
    var NS = 'http://www.w3.org/2000/svg';
    /* ⛔ BUILT WITH createElementNS, NOT innerHTML. Assigning innerHTML on an
       SVG element is the likeliest reason the whole picker rendered nothing on
       iPad Safari: if it throws, defs() throws, build() throws, and mount()
       dies silently -- on a device with no console to look at. DOM calls
       cannot throw here. */
    function el(name, attrs) {
      var n = document.createElementNS(NS, name);
      Object.keys(attrs || {}).forEach(function (k) { n.setAttribute(k, attrs[k]); });
      return n;
    }
    function grad(id, stops) {
      var g = el('linearGradient', { id: id, x1: '0', y1: '0', x2: '0.35', y2: '1' });
      stops.forEach(function (st) { g.appendChild(el('stop', { offset: st[0], 'stop-color': st[1] })); });
      return g;
    }
    var svg = el('svg', { id: 'pp-lp-defs', 'aria-hidden': 'true', width: '0', height: '0' });
    svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden';
    var dd = el('defs', {});
    dd.appendChild(grad('pp-lp-photo', [['0%', '#CBDCF1'], ['55%', '#B2C9E6'], ['100%', '#9FBBDD']]));
    dd.appendChild(grad('pp-lp-photo-dark', [['0%', '#6C7583'], ['55%', '#5A6270'], ['100%', '#4C535F']]));
    svg.appendChild(dd);
    document.body.appendChild(svg);
  }

  /* ── The product stays beside the choice ───────────────────────────────
     Jeff: "dont let the product photo scroll behind the header like this
     unless it needs to. same with the product name. the description can
     scroll off screen, but try to keep the name above the layout options."

     With eighteen tiles the grid is taller than the viewport, so by the time
     you are choosing, the name and photo have scrolled away and the page is a
     wall of anonymous swatches. This thin bar — thumbnail, name, price — sits
     immediately above the tiles, where it cannot be separated from them. The
     description is left to scroll off, as he said.
     (Sticky was tried and is broken in this theme — see the CSS note.)

     Everything is read off the page we are already on, so nothing here can
     disagree with the product page. */
  /* caption mode: the theme's own gallery column is `position: sticky`, so a
     name-and-price line appended INSIDE it travels down the page with the
     photo for free -- no fixed positioning, no scroll listener, no fight with
     the mega menu, and above all nothing floating over the tiles. The
     hand-rolled pin below is still what runs when the picker is not beside a
     sticky gallery. */
  function captionInGallery() {
    var media = document.querySelector('.product-desktop-media');
    if (!media || getComputedStyle(media).position !== 'sticky') return false;
    var bar = buildBar();
    bar.classList.add('pp-lp__pin--cap');
    media.appendChild(bar);

    /* ⚠ AT REST THE CAPTION REPEATS THE TITLE SITTING RIGHT BESIDE IT. It only
       earns its place once the real title has scrolled away, so it fades in
       then -- with OPACITY, not display: the caption is in the sticky
       gallery's flow and hiding it would shift the description under it by its
       own height every time you crossed the threshold. */
    var realTitle = document.querySelector('.product__info h1, .product__title');
    if (realTitle && window.IntersectionObserver) {
      try {
        new IntersectionObserver(function (e) {
          bar.classList.toggle('is-shown', !e[0].isIntersecting);
        }, { threshold: 0 }).observe(realTitle);
      } catch (e) { bar.classList.add('is-shown'); }
    } else {
      bar.classList.add('is-shown');
    }

    /* Jeff: "maybe the desc can go under the image on desktop."
       The right-hand column is for choosing -- price, color, layouts -- and
       the description is reading matter. Under the photo it fills the space
       the sticky gallery leaves empty and stops pushing the tiles down.

       ⚠ The gallery cell IS the sticky element, so this lands inside it and
       rides along. Measured after the move to confirm the block stays shorter
       than the viewport; a sticky box taller than the screen stops holding. */
    var desc = document.querySelector('.product__info .block-description');
    if (desc && desc.parentNode) {
      desc.classList.add('pp-lp__desc');
      media.appendChild(desc);
    }
    return true;
  }

  function buildBar() {
    var bar = document.createElement('div');
    bar.className = 'pp-lp__pin';

    var img = document.querySelector('.product-gallery img, .product__media img, .product-media img, main img');
    if (img && (img.currentSrc || img.src)) {
      var t = document.createElement('img');
      t.className = 'pp-lp__pinimg';
      t.src = img.currentSrc || img.src;
      t.alt = '';
      t.setAttribute('aria-hidden', 'true');
      bar.appendChild(t);
    }

    var txt = document.createElement('div');
    txt.className = 'pp-lp__pintext';

    var titleEl = document.querySelector('.product__title, .product-title, h1');
    var nm = document.createElement('span');
    nm.className = 'pp-lp__pinname';
    nm.textContent = titleEl ? titleEl.textContent.trim() : '';
    txt.appendChild(nm);

    var priceEl = document.querySelector('.product__price, .price, [class*="price"]');
    if (priceEl) {
      var pr = (priceEl.textContent || '').replace(/\s+/g, ' ').trim();
      if (pr && pr.length < 24) {
        var ps = document.createElement('span');
        ps.className = 'pp-lp__pinprice';
        ps.textContent = pr;
        txt.appendChild(ps);
      }
    }
    bar.appendChild(txt);

    return bar;
  }


  /* ⛔ DO NOT MEASURE THE HEADER. THE THEME ALREADY PUBLISHES IT.
     Jeff: "It's not sticking."

     Two separate mistakes were doing that, and both came from measuring:
     `headerBottom()` read the header's box on scroll and returned 0 whenever
     the header's computed position was not sticky/fixed -- which Aurora's
     float-on-scroll-up header does routinely -- so the offset flipped between
     119 and 204 while you scrolled, and the head jumped 85px. And the value
     it cached at mount, --pp-header-bottom: 111px, was the pre-layout reading,
     85px short of the real 196.

     Aurora keeps `--gsc-header-bottom-side` on :root and it is CORRECT AND
     LIVE -- measured at 196.1875px at rest and still 196.1875px at
     scrollY 985, where headerBottom() was returning 0. So the offset is now
     pure CSS off that variable and there is nothing left to measure, cache or
     re-measure. */
  function stickyHead(head, sentinel) {
    /* The sentinel is no longer needed for the stuck test -- the head's own
       rect against its offset answers it, and that reading is live. */
    sentinel.remove();
    var queued = false;
    function place() {
      queued = false;
      /* the element's own resolved `top` -- whatever CSS worked it out to be */
      var t = parseFloat(getComputedStyle(head).top) || 0;
      head.classList.toggle('is-stuck', head.getBoundingClientRect().top <= t + 1);
    }
    function onScroll() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(place);
    }
    place();
    /* ⚠ capture on document: scroll does not bubble, and which element
       actually scrolls this theme is ambiguous. */
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    window.addEventListener('load', onScroll);
    setTimeout(place, 400);
    setTimeout(place, 1500);
  }

  function build() {
    defs();
    var sec = document.createElement('section');
    sec.className = 'pp-lp';
    sec.setAttribute('aria-labelledby', 'pp-lp-h');

    /* ⛔ NOT BOTH. A fixed bar over a 1,500px grid of tiles covers a whole
       row of them, which is worse than the problem it solves. */
    /* ⛔ NO MORE HAND-ROLLED PIN. Jeff: "Tabs should be sticky under the
       product name. And make the product name look less like a button."
       Both complaints were the same bug: the bar went `position: fixed` on its
       own schedule while the heading and tabs became sticky on theirs, so it
       floated OVER them -- and to survive floating over the tiles it needed a
       card, a border and a shadow, which is what made it read as a button.

       Put it INSIDE the sticky head and both go away: one sticky unit, name
       above tabs, nothing overlapping, so it needs no surface of its own. The
       whole fixed-position mechanism -- ~100 lines of scroll math, header
       measuring and mega-menu dodging -- is deleted rather than repaired. */
    /* ⚠ EXACTLY TWO CHILDREN: the sticky bar, then everything else in one
       wrapper. With the heading, subtitle and grid as loose siblings of the
       sticky bar, their boxes interleaved — measured, the bar landed at y=821
       while the heading it is supposed to sit above was at y=802. Whatever
       Aurora does to the containing column, a single in-flow sibling cannot
       interleave with it. */
    var body = document.createElement('div');
    body.className = 'pp-lp__body';

    /* ── THE HEADING AND TABS STICK ──────────────────────────────────────────
       Jeff circled the heading, the line under it and the tab bar: "make this
       sticky." Right -- a tab row you have to scroll back up to reach is a tab
       row you stop using once the grid is 1,500px tall.

       ⚠ REAL CSS STICKY, and it works because the conditions were measured:
       every ancestor from .pp-lp__body up to <body> computes
       `overflow: visible`, and <body> is the scroll host. `top` matches the
       theme's own gallery to the pixel (header bottom + 12, which computed to
       124px), so the two stick to the same line.

       ⛔ An earlier attempt concluded CSS sticky was broken on this page. It
       was not -- that was the OLD structure, where the bar was a loose sibling
       of the heading and grid. With everything inside .pp-lp__body it holds;
       verified by measuring the stuck position, not assumed. The hand-rolled
       fixed-position bar it led to has since been deleted.

       The sentinel is the standard way to know it is stuck WITHOUT a scroll
       listener: a zero-height marker directly above, watched by an
       IntersectionObserver. Once stuck, the instructional line collapses --
       it has been read by then, and 110px of permanent chrome on a tablet is
       too much to hold the screen. */
    var sentinel = document.createElement('div');
    sentinel.className = 'pp-lp__sentinel';
    body.appendChild(sentinel);

    var head = document.createElement('div');
    head.className = 'pp-lp__head';

    /* The product name and price ride ALONG WITH the tabs, as the first child
       of the one sticky box -- not as a second thing that sticks on its own. */
    if (!CAPTION) {
      var hb = buildBar();
      hb.classList.add('pp-lp__pin--head');
      head.appendChild(hb);
    }

    /* ⛔ ONLY A BOOK HAS PAGES. Jeff: "'You can change any page's layout once
       you are inside.' doesnt make sense for anything but a book." Right --
       and on a mug these are not layouts either, they are designs. The test is
       the theme's own package, which is the only place the catalog says
       whether the thing has pages. */
    var isBook = CARDS.some(function (c) { return /photobook/i.test(c.theme || ''); });

    var h = document.createElement('h2');
    h.className = 'pp-lp__h';
    h.id = 'pp-lp-h';
    h.textContent = isBook ? 'Pick a starting layout' : 'Pick a design';
    head.appendChild(h);

    var sub = document.createElement('p');
    sub.className = 'pp-lp__sub';
    /* ⚠ The theme itself cannot be changed once inside -- allowChangeTheme is
       off for this store -- so neither line promises that. */
    sub.textContent = isBook
      ? 'You can change any page’s layout once you are inside.'
      : 'You can swap the photos and edit the text once you are inside.';
    head.appendChild(sub);

    /* ── TABS ────────────────────────────────────────────────────────────────
       Jeff, on the 8x8 book: "add tabbed categories of sections on the 8x8.
       its too chaotic." Forty-eight tiles in one wall is chaotic.

       ⚠ The groups are DATA, not a guess in here. For cards and mugs they are
       Mediaclip's own categories, harvested from getthemes; for the photo
       books, which Mediaclip ships as one unnamed bucket, they are ours. A set
       with no groups in the file renders exactly as before -- no bar, no
       hidden cards. See the header of cats.py.

       ⛔ NO "All" TAB. Switching to it would load every render at once, which
       is the payload the tabs exist to avoid, and 48 tiles in one wall is the
       thing being fixed. */
    var TABS = (DATA && DATA.tabs && SET_ID) ? DATA.tabs[SET_ID] : null;
    /* ⛔ ONE TAB IS NOT A CHOICE. A tablist with a single chip is a label
       pretending to be a control, and it would also leave the sticky band
       carrying nothing but itself. Jeff: "the tab bar shouldnt show if there
       are no tabs to sort things by." Today every set in pp-sets.json has 3 or
       more, so this is the guard for the next set somebody adds. */
    if (TABS && TABS.length < 2) TABS = null;
    var tabOf = {};
    if (TABS) {
      TABS.forEach(function (t, ti) {
        t[1].forEach(function (ci) { tabOf[ci] = ti; });
      });
    }

    var grid = document.createElement('div');
    grid.className = 'pp-lp__grid';
    /* A grid of real renders needs wider columns than a grid of drawings. */
    if (CARDS.some(function (c) { return !!c.img; })) {
      grid.className += ' pp-lp__grid--shots';
      /* ⚠ Reserve the height BEFORE the images load. Every render in a set is
         made against the same product, so one ratio holds for the whole grid;
         without it a 48-card grid reflows 48 times as the lazy images land. */
      var a = DATA && DATA.ar && SET_ID ? DATA.ar[SET_ID] : 0;
      if (a) grid.style.setProperty('--pp-lp-ar', String(a));
    }

    CARDS.forEach(function (c, i) {
      var card = document.createElement('div');
      card.className = 'pp-lp__card';
      /* the shade is fixed per theme now, not a toggle */
      card.setAttribute('data-shade', c.dark ? 'dark' : 'light');
      card.style.setProperty('--pp-tilt', [-2.6, 1.9, -1.2, 2.8, -3.2, 1.3, -2][i % 7] + 'deg');

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pp-lp__pick';
      if (c.img) {
        /* Mediaclip's own render. Transparent ground, so the plate still
           shows through and the shadow follows the artwork, not a box. */
        var im = document.createElement('img');
        im.className = 'pp-lp__art pp-lp__shot';
        im.src = c.img + '?width=440';
        /* Only the cards that are actually on screen at mount: a hidden tab
           never fetches, which is most of why the tabs help here. */
        im.loading = (!TABS || tabOf[i] === 0) && i < 8 ? 'eager' : 'lazy';
        im.decoding = 'async';
        im.alt = '';
        btn.appendChild(im);
        btn.classList.add('pp-lp__pick--shot');
      } else {
        btn.innerHTML = art(c.art);
      }
      btn.setAttribute('aria-label', 'Start with ' + c.name);
      btn.addEventListener('click', function () {
        if (btn.getAttribute('data-busy')) return;
        btn.setAttribute('data-busy', '1');
        card.classList.add('is-busy');
        launch(c.theme);
      });
      card.appendChild(btn);

      var meta = document.createElement('div');
      meta.className = 'pp-lp__meta';

      var nm = document.createElement('span');
      nm.className = 'pp-lp__name';
      nm.textContent = c.name;
      meta.appendChild(nm);

      if (c.note) {
        var nt = document.createElement('span');
        nt.className = 'pp-lp__note';
        nt.textContent = c.note;
        meta.appendChild(nt);
      }

      card.appendChild(meta);
      grid.appendChild(card);
    });

    if (TABS) {
      var bar = document.createElement('div');
      bar.className = 'pp-lp__tabs';
      bar.setAttribute('role', 'tablist');
      bar.setAttribute('aria-label', 'Layout categories');
      var btns = [];
      function show(ti) {
        btns.forEach(function (b, i) {
          var on = i === ti;
          b.setAttribute('aria-selected', on ? 'true' : 'false');
          b.tabIndex = on ? 0 : -1;
        });
        [].forEach.call(grid.children, function (card, ci) {
          card.classList.toggle('is-off', tabOf[ci] !== ti);
        });
        /* Jeff: "make the layouts list snap to the top when you change the
           list tab." Switching from a 55-tile tab to a 2-tile one otherwise
           leaves you staring at empty column below the grid.

           ⚠ ONLY WHEN ALREADY SCROLLED PAST THE TOP. If the list is already
           on screen, scrolling would yank the page for no reason -- and the
           first show(0) at build time must not move anything at all.
           ⚠ scrollIntoView, not window.scrollTo: which element scrolls this
           theme is ambiguous and scrollTo(0, n) measured as a no-op here.
           scroll-margin-top carries the sticky offset so the heading lands
           below the site header rather than under it. */
        if (!head.classList.contains('is-stuck')) return;
        /* ⛔ DO NOT SCROLL THE STICKY HEAD ITSELF, and do not scroll in the
           same frame as the toggles. Measured: switching a 236-tile tab for an
           18-tile one collapses .pp-lp__body, the head's sticky range ends,
           and scrollIntoView on it sent the page DOWN 1,174px with the grid
           1,636px above the viewport.

           The section is in normal flow and its top does not move when the
           grid's height changes, so that is the anchor; one frame later the
           new height has settled. */
        requestAnimationFrame(function () {
          var off = parseFloat(getComputedStyle(head).top) || 0;
          var host = document.scrollingElement || document.documentElement;
          var want = Math.max(0, Math.round(sec.getBoundingClientRect().top + host.scrollTop - off));
          var still = window.matchMedia &&
                      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          /* Compute the target rather than let scrollIntoView infer it: the
             offset is the sticky heading's own live top, so the list lands
             exactly under it. */
          try {
            host.scrollTo({ top: want, behavior: still ? 'auto' : 'smooth' });
          } catch (e) {
            host.scrollTop = want;
          }
          /* ⚠ Belt and braces. `html` here is the scrolling element and has a
             4,835px range, yet every programmatic write is ignored while the
             tab is in the background -- so this could not be verified in an
             automated tab, only reasoned about. scrollIntoView goes through a
             different path, so try it too when the position did not take. */
          if (Math.abs(host.scrollTop - want) > 4) {
            sec.style.scrollMarginTop = off + 'px';
            try { sec.scrollIntoView({ block: 'start', behavior: still ? 'auto' : 'smooth' }); }
            catch (e2) { sec.scrollIntoView(true); }
          }
        });
      }
      TABS.forEach(function (t, ti) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'pp-lp__tab';
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-selected', ti === 0 ? 'true' : 'false');
        b.tabIndex = ti === 0 ? 0 : -1;
        b.innerHTML = '';
        b.appendChild(document.createTextNode(t[0]));
        var c = document.createElement('span');
        c.className = 'pp-lp__tabn';
        c.textContent = String(t[1].length);
        b.appendChild(c);
        b.addEventListener('click', function () { show(ti); });
        b.addEventListener('keydown', function (e) {
          var d = e.key === 'ArrowRight' ? 1 : (e.key === 'ArrowLeft' ? -1 : 0);
          if (!d) return;
          e.preventDefault();
          var n = (ti + d + btns.length) % btns.length;
          show(n); btns[n].focus();
        });
        btns.push(b);
        bar.appendChild(b);
      });
      head.appendChild(bar);
      body.appendChild(head);
      body.appendChild(grid);
      show(0);
    } else {
      body.appendChild(head);
      body.appendChild(grid);
    }
    stickyHead(head, sentinel);
    sec.appendChild(body);

    /* ⛔ NO "browse all designs" LINK. Jeff: "put all the layouts on this
       page. i don't want that link to the all layouts page." All eighteen of
       Mediaclip's themes are reachable from these twelve cards — six of them
       are light/dark pairs of one design and carry both identifiers on the
       shade dots — so the link led nowhere the customer could not already
       get, while offering a route back to the chooser this exists to skip. */

    return sec;
}

  /* OFF BY DEFAULT. Jeff wants to look at this before customers do, and a
     client-side flag is cheaper than a duplicate theme: `?pp_lp=1` turns it on
     and remembers, `?pp_lp=0` turns it back off. Customers see no change.
     ⚠ A visibility flag, NOT a security boundary — it hides UI, nothing else.

     ON as of 2026-09-14 at Jeff's instruction. `?pp_lp=0` still turns it off
     for one visit; set DEFAULT_ON back to false to retract it entirely. */
  var DEFAULT_ON = true;

  /* ⚠ COOKIE AS WELL AS localStorage, and the hash counts too.
     Jeff turned the flag on and still saw nothing, in a browser where it
     demonstrably mounts. localStorage is the fragile part: it is per-browser,
     it is blocked outright in some in-app and private contexts, and it is
     silently empty if the link ever loses its query string. A cookie survives
     more of those and is shared across tabs, and `#pp_lp=1` survives link
     handlers that strip the query. */
  function setFlag(on) {
    try {
      if (on) localStorage.setItem('pp_lp', '1');
      else localStorage.removeItem('pp_lp');
    } catch (e) {}
    try {
      document.cookie = 'pp_lp=' + (on ? '1' : '') +
        '; path=/; max-age=' + (on ? 7776000 : 0) + '; samesite=lax';
    } catch (e) {}
  }

  function enabled() {
    var q = location.search + location.hash;
    if (/[?&#]pp_lp=1/.test(q)) { setFlag(true); return true; }
    if (/[?&#]pp_lp=0/.test(q)) { setFlag(false); return false; }
    try { if (localStorage.getItem('pp_lp') === '1') return true; } catch (e) {}
    if (/(^|;\s*)pp_lp=1(;|$)/.test(document.cookie || '')) return true;
    return DEFAULT_ON;
  }

  var said = false;
  function say(msg) {
    if (said) return;
    said = true;
    try { console.log('[pp-layoutpicker] ' + msg); } catch (e) {}
  }

  /* Aurora's own product gallery asks for `top: 60px` while its header is
     176px tall, so the photo tucks up under the header. Same variable, same
     one-liner, and it needs no listener because CSS re-resolves it itself. */
  function fixStickyGallery() {
    var g = document.querySelector('.product-desktop-media');
    if (!g || getComputedStyle(g).position !== 'sticky') return;
    g.style.top = 'calc(var(--gsc-header-bottom-side, 196px) + 12px)';
  }

  /* ── A panel you can read on an iPad ───────────────────────────────────
     Jeff: "not working for me on ipad. even in private mode. i need a way to
     interact with this."

     Private mode rules out the cache AND the flag, so it was failing on the
     device itself, silently — and a console breadcrumb is useless on an iPad.
     So the diagnosis goes ON THE PAGE. `?pp_lp=debug` always shows it;
     otherwise it appears only when something actually threw, so a customer
     never sees it. */
  function panel(rows, bad) {
    var d = document.createElement('div');
    d.className = 'pp-lp__diag' + (bad ? ' pp-lp__diag--bad' : '');
    var h = document.createElement('strong');
    h.textContent = bad ? 'Layout picker did not load' : 'Layout picker diagnostics';
    d.appendChild(h);
    var ul = document.createElement('ul');
    rows.forEach(function (r) {
      var li = document.createElement('li');
      li.textContent = r;
      ul.appendChild(li);
    });
    d.appendChild(ul);
    return d;
  }

  function showPanel(rows, bad) {
    try {
      if (document.querySelector('.pp-lp__diag')) return;
      var el = panel(rows, bad);
      /* ⚠ In debug mode this goes at the TOP OF THE SCREEN, not into the
         flow. Jeff's reply to the first version was "i dont see a console" —
         he was looking for devtools because a panel sitting halfway down a
         long product page is not discoverable. In debug mode the point is to
         be impossible to miss. An error panel outside debug mode still sits
         in the flow, next to the thing that failed. */
      if (debugOn()) {
        el.className += ' pp-lp__diag--pinned';
        var x = document.createElement('button');
        x.type = 'button';
        x.className = 'pp-lp__diagx';
        x.textContent = 'Close';
        x.addEventListener('click', function () { el.remove(); });
        el.appendChild(x);
        document.body.appendChild(el);
        return;
      }
      var real = document.querySelector('button[data-is-customize-button="true"]');
      var host = (real && real.closest('form')) || document.querySelector('main') || document.body;
      if (host.parentNode) host.parentNode.insertBefore(el, host.nextSibling);
      else host.appendChild(el);
    } catch (e) {}
  }

  function debugOn() { return /[?&#]pp_lp=debug/.test(location.search + location.hash); }

  function checks() {
    return ['version ' + VERSION,
            'handle: ' + handle(),
            'package: ' + productPackage(),
            'variant: ' + variantId(),
            'sets file: ' + DATA_STATE + (DATA && DATA.lists ? ' (' + Object.keys(DATA.lists).length + ' lists)' : ''),
            'set source: ' + (listFromData() ? ('per-product ' + SET_ID) : (SETS[productPackage()] ? 'package fallback' : 'none')),
            'flag on: ' + enabled(),
            'customize button: ' + !!document.querySelector('button[data-is-customize-button="true"]'),
            'UA: ' + String(navigator.userAgent).slice(0, 64)];
  }

  function mount() {
    if (document.querySelector('.pp-lp')) return;
    /* ⚠ Mount AFTER the set file settles, not before. Mounting first would
       paint the two-card package fallback and then never replace it, because
       mount() returns early once .pp-lp exists. loadSets calls back either
       way, and caps itself at 4s. */
    if (DATA_STATE === 'idle' || DATA_STATE === 'pending') {
      loadSets(mount);
      return;
    }
    if (!enabled()) {
      say('off — flag not set.');
      if (debugOn()) showPanel(checks().concat(['stopped: flag off']), false);
      return;
    }
    var pick = cardsForThisProduct();
    if (!pick.cards) {
      say('off — package "' + pick.pkg + '" has no picker set.');
      if (debugOn()) showPanel(checks().concat(['stopped: package "' + pick.pkg + '" not in SETS']), false);
      return;
    }
    CARDS = pick.cards;
    var real = document.querySelector('button[data-is-customize-button="true"]');
    if (!real) {
      if (debugOn()) showPanel(checks().concat(['stopped: no customize button yet']), false);
      return;
    }
    var form = real.closest('form');
    var anchor = form || real;

    /* ⛔ THE PICKER GOES ABOVE THE BUTTON, AND THE BUTTON GOES AWAY.
       First version put it after the form: Jeff opened the page, saw
       "Personalize this item", clicked it, landed on the old chooser and
       reported the page as unchanged. He was right — a picker underneath the
       primary call to action is not the call to action, it is an addendum
       nobody scrolls to. "You select your variation right there and are taken
       to the editor" means the picker IS the button.

       ⚠ HIDDEN WITH CSS, NOT REMOVED. launch() reads the product and variant
       ids out of that button's own form, and pp-personalize.js reads the
       button to build the sticky bar, so the element has to stay in the DOM.
       The class also hides the sticky bar, which would otherwise offer a
       second path straight back to the chooser. */
    /* ⚠ try/catch around the BUILD. Anything throwing in there used to kill
       the mount with no trace; the reason now lands on the page where it can
       be read on a tablet. */
    /* Decided BEFORE build(), because it changes what build() puts in.
       ⚠ THE GALLERY MUST ACTUALLY BE STICKY, not merely present. Measured at
       390px: .product-desktop-media is still in the DOM but static (the theme
       swaps to .product-mobile-media), so a caption appended to it would sit
       in the page and scroll away -- and because build() had already skipped
       the pin slot, a phone got NEITHER. That loses what Jeff asked for twice
       ("the image and product name do not stick"). No sticky gallery means the
       hand-rolled pin does the job instead. */
    var media = document.querySelector('.product-desktop-media');
    CAPTION = CARDS.some(function (c) { return !!c.img; }) &&
              !!document.querySelector('.product__info .block-price') &&
              !!media && getComputedStyle(media).position === 'sticky';

    var built;
    try {
      built = build();
    } catch (err) {
      showPanel(checks().concat(['ERROR: ' + (err && err.message ? err.message : String(err))]), true);
      say('FAILED: ' + (err && err.message));
      return;
    }
    document.documentElement.classList.add('pp-lp-on');

    /* ⛔ A BIG SET DOES NOT FIT THE INFO COLUMN, AND THAT IS MEASURED.
       Eighteen drawn swatches at 132px fit the 530px product-info column
       three across. Forty-eight real previews at 2:1 fit TWO across, which
       made the grid 3,870px tall beside an empty half-page. So a large
       image-backed set mounts full width, immediately under the photo-and-
       details block, inside .product__container (1320px -- the page's own
       content width, not the 2550px section, and its overflow is visible at
       every width I measured).

       The Personalize button stays hidden either way, so the picker is still
       the only way forward -- the thing Jeff objected to the first time was a
       picker sitting BELOW a live "Personalize this item" button, not a
       picker below the product details. The pinned bar carries the photo and
       name down the page with it. */
    /* ⛔ FULL WIDTH BELOW THE PRODUCT BLOCK WAS WRONG, AND IT IS MEASURED.
       Jeff: "the book options are too low on the screen. they feel unrelated
       to the product." He is describing this, at 2560x1215: the photo-and-
       details block is 576px tall, the picker began at y=805 and its FIRST
       TILE at y=1092 -- a whole screen below the product, behind a rule, with
       nothing tying the two together.

       So the picker goes back BESIDE the photo, in the details column, right
       under the price. The column is too narrow for forty-eight 2:1 renders
       at the theme's 660/660 split, so a product with an image set widens the
       details column (CSS, via .pp-lp-incol on <html>) and the gallery stays
       sticky alongside. The tabs are what make this fit: a tab holds 11-19
       tiles, not 48. */
    /* ── WHAT COMES FIRST ────────────────────────────────────────────────────
       Jeff: "move the colors above the layouts."

       On a mug, a frame or any other family, Fujifilm ships each color and
       each size as its own product and the theme's options block turns those
       siblings into a picker. That choice has to come first: the layouts are
       rendered on the object you picked, so choosing the object after the
       layout is backwards.

       So the picker goes after the OPTIONS block when the product has one,
       and after the price when it does not. Measured on the red mug: the
       options block is 238px of real content there and empty on a photo book,
       hence the text test rather than a height test -- at mount time nothing
       has been laid out yet. */
    var price = document.querySelector('.product__info .block-price');
    var opts = document.querySelector('.product__info .block-pp_options');
    var desc = document.querySelector('.product__info .block-description');
    if (opts && (opts.textContent || '').trim()) {
      /* Jeff: "the description should be above the color." That ordering is
         for the single-column case; on desktop captionInGallery() has already
         taken the description out to sit under the photo. */
      if (!CAPTION && desc && desc.parentNode) opts.parentNode.insertBefore(desc, opts);
      price = opts;
    }
    if (CAPTION && price && price.parentNode) {
      built.classList.add('pp-lp--incol');
      document.documentElement.classList.add('pp-lp-incol');
      if (CAPTION) captionInGallery();
      price.parentNode.insertBefore(built, price.nextSibling);
    } else {
      anchor.parentNode.insertBefore(built, anchor);
    }
    /* Jeff: "move the printed by fuji thing under the templates."
       It is the theme's own block and it is the last child of the product
       info column, so with the picker mounted full width below that column
       the note sat above the layouts instead of after them. Moved, not
       copied -- one node, still the theme's markup and styles. */
    try {
      var fuji = document.querySelector('.product__info .block-fuji_tag');
      if (fuji && built.querySelector('.pp-lp__body')) {
        fuji.classList.add('pp-lp__after');
        built.querySelector('.pp-lp__body').appendChild(fuji);
      }
    } catch (e) {}

    said = false;
    say('mounted, ' + CARDS.length + ' layouts.');
    if (debugOn()) showPanel(checks().concat(['mounted ' + CARDS.length + ' layouts']), false);
  }

  /* ⛔ MOUNT ONCE WAS A BUG, AND IT IS WHY JEFF SAW NOTHING.
     The first version called mount() once on DOMContentLoaded. Aurora
     hydrates the product form from custom elements, so on a cold load the
     button carrying data-is-customize-button does not exist yet, mount()
     finds nothing, returns, and is never called again — silently. It worked in
     a tab I had reloaded a dozen times (warm cache, fast hydration) and did
     nothing on a real first visit. pp-personalize.js already uses an observer
     for exactly this; this one now does too.

     The observer also re-mounts if Aurora re-renders the section (a variant
     change tears out and rebuilds that subtree). Both guards inside mount()
     keep it cheap: it returns immediately once .pp-lp is present. */
  function boot() {
    /* the gallery fix is independent of the picker and runs on every product
       page, so it cannot be gated behind the handle list or the flag */
    fixStickyGallery();
    mount();
    if (document.querySelector('.pp-lp')) return;
    var queued = false;
    var mo = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        fixStickyGallery();
        mount();
      });
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
    /* Stop watching once it is up and the page has settled; the picker
       re-mounts on its own if the section is rebuilt, because mount() is
       called again by the observer until then. */
    window.addEventListener('load', function () {
      setTimeout(function () { if (document.querySelector('.pp-lp')) mo.disconnect(); }, 4000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
